import { createSelector } from '@reduxjs/toolkit';
import { PhoneHelpers, UserHelpers } from '../helpers';
import { Types } from '../constants';

const getUsers = state => state.user.users;
const getUserIds = state => state.user.userIds;
const getTypeFilter = state => state.user.typeFilter;
const getTagFilter = state => state.user.tagFilter;
const getLocationFilter = state => state.user.locationFilter;
const getInboxUserId = state => state.inbox.userId;
const getChatUserId = state => state.chat.userId;
const getUserUserId = state => state.user.userId;
const getPathName = (state, pathname) => pathname;
const getChannels = state => state.channel.channels;
const getChannelIds = state => state.channel.channelIds;
const getOrganizations = state => state.organization.organizations;
const getPhones = state => state.phone.phones;
const getFacebooks = state => state.facebook.facebooks;
const getRhinograms = state => state.rhinogram.rhinograms;
const getTypes = state => state.type.types;
const getTypeIds = state => state.type.typeIds;
const getLoggedInUserId = state => state.auth.currentUser;

export const getLoggedInUser = createSelector(
  [getUsers, getLoggedInUserId],
  (users, loggedInUserId) => {
    if (users && loggedInUserId) {
      return users[loggedInUserId];
    }

    return null;
  },
);

export const getLoggedInUserPermissionNames = createSelector(
  [getUsers, getLoggedInUserId],
  (users, loggedInUserId) => {
    if (users && loggedInUserId) {
      return users[loggedInUserId].permissions.map(permission => permission.name);
    } else {
      return [];
    }
  },
);

export const getActiveUser = createSelector(
  [getUsers, getInboxUserId, getChatUserId, getUserUserId, getPathName, getLoggedInUserId],
  (users, inboxUserId, chatUserId, userUserId, pathName, loggedInUserId) => {
    let retUser = null;
    if (users) {
      if (pathName.startsWith('/inbox') && inboxUserId) {
        retUser = users[inboxUserId];
      } else if (pathName.startsWith('/chat') && chatUserId) {
        retUser = users[chatUserId];
      } else if (pathName.startsWith('/contacts') && userUserId) {
        retUser = users[userUserId];
      } else if (pathName.startsWith('/settings/organization/library/templates') && loggedInUserId) {
        retUser = users[loggedInUserId];
      } else if (pathName.startsWith('/settings/organization/appointment-manager') && loggedInUserId) {
        retUser = users[loggedInUserId];
      }
    }
    return retUser;
  },
);

const getActiveChannelIds = createSelector(
  [getChannels, getChannelIds],
  (channels, channelIds) =>
    channelIds.filter(id => !channels[id].deleted),
);

export const getActiveUserContactInfo = createSelector(
  [getActiveUser, getPhones, getFacebooks, getRhinograms, getChannels, getActiveChannelIds],
  (user, phones, facebooks, rhinograms, channels, channelIds) => {
    let userContactInfo = [];
    if (user && (!user.phones || !user.facebooks || !user.rhinograms)) return userContactInfo;

    const createUserContactInfo = (id, name, className, type, isOwner) => ({
      id,
      name,
      type,
      isOwner,
      class: className,
      fullId: `${className}-${id}`,
    });
    const createUserContactInfoPhone = (id) => {
      const phone = phones[id];
      if (!phone) return null;

      const className = 'phone';
      const type = 'sms';
      const name = PhoneHelpers.formatPhone(phone.value);
      const isOwner = phone.ownerId === user.id;

      return createUserContactInfo(id, name, className, type, isOwner);
    };

    const createUserContactInfoFacebook = (id) => {
      const facebook = facebooks[id];
      if (!facebook) return null;

      // We need to first confirm that our activeChannelIds array includes the facebook.channelId that is being returned.
      // This check confirms that the activeUser's facebook has permission to communicate with the facbeook page.
      if (!channelIds.includes(facebook.channelId)) {
        return {};
      }

      const className = 'facebook';
      const type = 'facebook';
      const name = facebook.value;
      // isOwner will always be true for facebooks.
      const isOwner = true;

      return createUserContactInfo(id, name, className, type, isOwner);
    };

    const createUserContactInfoRhinogram = (id) => {
      const rhinogram = rhinograms[id];
      if (!rhinogram) return null;

      // We need to first confirm that our activeChannelIds array includes the rhinogram.channelId that is being returned.
      // This check confirms that the activeUser's rhinogram has permission to communicate with the rhinogram channel.
      if (!channelIds.includes(rhinogram.channelId)) {
        return {};
      }

      const className = 'rhinogram';
      const type = 'rhinogram';
      const name = rhinogram.value;
      // isOwner will always be true for facebooks.
      const isOwner = true;

      return createUserContactInfo(id, name, className, type, isOwner);
    };

    /**
     * Returns true if any of the organizations's channels support
     * the given contact info class. Returns false otherwise.
     */
    const isValidContactClassForChannels = contactInfo =>
      channelIds.some(channelId => channels[channelId].incomingTypeClass?.className === contactInfo.class);

    // build contact communication object array to use in 'To' Dropdown
    if (user) {
      userContactInfo = user.phones
        .map(createUserContactInfoPhone);

      user.facebooks.forEach((f) => {
        userContactInfo = facebooks[f] ? userContactInfo.concat(createUserContactInfoFacebook(f)) : userContactInfo;
      });

      user.rhinograms.forEach((r) => {
        userContactInfo = facebooks[r] ? userContactInfo.concat(createUserContactInfoRhinogram(r)) : userContactInfo;
      });
    }

    return userContactInfo.filter(isValidContactClassForChannels).filter(c => c.isOwner);
  },
);

export const getLoggedInUserOrganization = createSelector(
  [getLoggedInUser, getOrganizations],
  (user, organizations) => {
    let userOrgInfo = {};

    if (user && organizations) {
      userOrgInfo = organizations[user.organization];
    }
    return userOrgInfo;
  },
);

export const getVisibleContacts = createSelector(
  [getUserIds, getUsers, getTypeFilter, getTagFilter, getLocationFilter, getTypes],
  (userIds, users, typeFilter, tagFilter, locationFilter) => {
    if (!typeFilter && !tagFilter && !locationFilter) {
      // We want to make sure not to return members in the getVisibleContacts selector.
      // There are cases where a member could potentially exist in our redux.userIds.
      return userIds.filter(id => users[id].typeId !== Types.TYPE_MEMBER);
    }

    let filteredUsers = userIds;

    if (typeFilter && typeFilter > -1) {
      filteredUsers = filteredUsers.filter(id => users[id].typeId === typeFilter);
    }

    return filteredUsers;
  },
);

export const getUserFilterTypes = createSelector(
  [getTypes, getTypeIds],
  (types, typeIds) => {
    let filterTypes = [];

    if (types && typeIds) {
      filterTypes = typeIds.filter(id => types[id].class === 'user');
    }

    return filterTypes;
  },
);

export const getUserMemberStatus = createSelector(
  [getActiveUser], user => UserHelpers.isMember(user),
);

export const getUserPatientStatus = createSelector(
  [getActiveUser], user => UserHelpers.isPatient(user) || UserHelpers.isOther(user),
);

export const getActiveMemberIds = createSelector(
  [getUserIds, getUsers],
  (userIds, users) =>
    userIds
      .filter(id => users[id].typeId === Types.TYPE_MEMBER && users[id].active),
);

export const getInActiveMemberIds = createSelector(
  [getUserIds, getUsers],
  (userIds, users) =>
    userIds
      .filter(id => users[id].typeId === Types.TYPE_MEMBER && !users[id].active),
);
